import { Info, Loader2, XCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectCreateOrderStatus,
  // selectOrderPromotion,
  selectOrderPromotionVoucher,
  selectStepStates,
  selectSubmitLoading,
} from "@/store/selectors/order.selector";
import {
  selectOperators,
  selectPhoneDetails,
  selectSelectedOperator,
  selectSelectedPlan,
} from "@/store/selectors/operator.selectors";
import { useTranslation } from "react-i18next";
import { OrderResponse, OrderResponseLineItem } from "@/store/types/order";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { setSubmitLoading, setCreatingOrder } from "@/store/slices/order.slice";
import {
  subscribeToNewsletter,
  performCreditCheck,
} from "@/store/thunks/customer.thunks";
import { finalizeOrder } from "@/store/thunks/order.thunks";
import {
  initiateCheckout,
  setPaymentMethod,
} from "@/store/thunks/payment.thunks";
import {
  selectBankIdContact,
  selectVerificationDetails,
} from "@/store/selectors/bankid.selectors";
import { CheckoutStep } from "@/lib/types/checkout-steps";
import {
  saveCheckout3dsSnapshot,
  clearCheckout3dsSnapshot,
  stripStripe3dsReturnParams,
} from "@/lib/stripe-3ds-snapshot";
import { isNonProduction, resolveFlexSiteUrl } from "@/lib/utils";

interface PaymentFormProps {
  order: OrderResponse;
  onSuccess: (orderId: string) => void;
  isActive?: boolean;
}

export default function PaymentForm({
  order,
  onSuccess,
  isActive = true,
}: PaymentFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams(); // Get the language parameter from URL

  const stripeSetupErrorMessages = useMemo(
    (): Record<string, string> => ({
      insufficient_funds: t("errors.INSUFFICIENT_FUNDS"),
      lost_card: t("errors.CARD_LOST_OR_STOLEN"),
      stolen_card: t("errors.CARD_LOST_OR_STOLEN"),
      generic_decline: t("errors.CARD_DECLINED"),
      do_not_honor: t("errors.CARD_DECLINED"),
      do_not_try_again: t("errors.CARD_DECLINED"),
      card_declined: t("errors.CARD_DECLINED"),
      expired_card: t("errors.CARD_EXPIRED"),
      incorrect_cvc: t("errors.INVALID_CARD"),
      incorrect_number: t("errors.INVALID_CARD"),
      invalid_cvc: t("errors.INVALID_CARD"),
      invalid_expiry_year: t("errors.INVALID_CARD"),
      invalid_expiry_month: t("errors.INVALID_CARD"),
      authentication_required: t("errors.AUTHENTICATION_REQUIRED"),
      setup_intent_authentication_failure: t("errors.AUTHENTICATION_REQUIRED"),
      processing_error: t("errors.PAYMENT_PROVIDER_ERROR"),
      card_not_supported: t("errors.PAYMENT_METHOD_REJECTED"),
    }),
    [t]
  );

  const [isReady, setIsReady] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState({
    terms: false,
    marketing: false,
  });

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();

  const operator = useAppSelector(selectSelectedOperator);
  const operators = useAppSelector(selectOperators);
  const selectedPlan = useAppSelector(selectSelectedPlan);
  const phoneDetails = useAppSelector(selectPhoneDetails);

  const operatorName = useMemo(() => {
    if (!operator || operator === "only-phone") return "";
    return (
      operators.find((op) => op.skuMatcher === operator)?.name || operator
    );
  }, [operator, operators]);

  const [error, setError] = useState<string | null>(null);
  const [needsRetry, setNeedsRetry] = useState(false);

  const submitLoading = useAppSelector(selectSubmitLoading);
  const createOrderStatus = useAppSelector(selectCreateOrderStatus);
  const verificationDetails = useAppSelector(selectVerificationDetails);
  const bankIdContact = useAppSelector(selectBankIdContact);
  const promotionVoucher = useAppSelector(selectOrderPromotionVoucher);
  const stepStates = useAppSelector(selectStepStates);
  // const promotion = useAppSelector(selectOrderPromotion);

  const shouldBypassCreditCheckForTest = (() => {
    const params = new URLSearchParams(window.location.search);
    const wantsSkipFromUrl =
      params.get("skip_bankid") === "1" ||
      params.get("skip-bankid") === "1" ||
      params.get("skip_step1") === "1" ||
      params.get("skip-step1") === "1" ||
      params.get("test_skip_bankid") === "1" ||
      params.get("test-skip-bankid") === "1";
    const wantsSkipFromSession =
      verificationDetails?.sessionId === "mock_skip_bankid" ||
      (() => {
        try {
          return sessionStorage.getItem(`skip_bankid_order_${order.id}`) === "1";
        } catch {
          return false;
        }
      })();

    return (wantsSkipFromUrl || wantsSkipFromSession) && isNonProduction();
  })();

  useEffect(() => {
    console.log("Stripe available:", !!stripe);
    console.log("Elements available:", !!elements);
  }, [stripe, elements]);

  const finalizeCheckoutAfterSetup = useCallback(
    async (customerId: string, paymentMethodId: string) => {
      const email = order.customer_email || bankIdContact.email;
      if (!email) {
        throw new Error(t("cardCheckout.missingCustomerEmail"));
      }
      if (!verificationDetails?.userAttributes.personalNumber) {
        throw new Error(t("cardCheckout.missingPersonalNumber"));
      }

      await dispatch(
        setPaymentMethod({
          customerId,
          paymentMethodId,
        })
      ).unwrap();

      const creditCheckResult = shouldBypassCreditCheckForTest
        ? { denied: false }
        : await dispatch(
            performCreditCheck(verificationDetails.userAttributes.personalNumber)
          ).unwrap();

      if (creditCheckResult.denied) {
        clearCheckout3dsSnapshot();
        dispatch(setSubmitLoading(false));
        navigate(`/${lang}/${order.id}/nekad`);
        return;
      }

      if (!order.line_items) {
        throw new Error("Missing line items");
      }

      const baseMonthlyPrice = parseInt(
        order.metadata?.["Flex Monthly Price"] || "0",
        10
      );
      if (!baseMonthlyPrice) {
        throw new Error("Missing monthly price information");
      }

      const bindingPeriod = parseInt(
        order.metadata?.["Flex Binding Period"] || "12",
        10
      );

      const filteredItems = order.line_items
        ?.filter((item: OrderResponseLineItem) =>
          item.sku_code?.startsWith("R")
        )
        .map((item: OrderResponseLineItem) => ({
          product_id: `${item.sku_code}_12`,
          sku: `${item.sku_code}_12`,
          name: item.name ?? "",
          quantity: item.quantity ?? 1,
          thumbnail: item.image_url ?? "",
          price: baseMonthlyPrice,
          voucher_code: promotionVoucher,
          subscription_duration: bindingPeriod,
        }));

      if (filteredItems.length === 0) {
        throw new Error("No valid items found in order");
      }
      const totalMonthlyAmount = filteredItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0
      );

      let operatorMetadata: Record<string, string> = {};
      if (selectedPlan && operator && operator !== "only-phone") {
        operatorMetadata = {
          operator_sku_matcher: operator,
          operator_plan: selectedPlan,
          operator_transfer_number_type: phoneDetails.numberType || "",
          operator_transfer_number: phoneDetails.phoneNumber || "",
        };
      }

      const orderMetadata = {
        ...Object.entries(order.metadata || {}).reduce((acc, [key, value]) => {
          if (key === "promotion") return acc;
          return {
            ...acc,
            [key]:
              typeof value === "object" ? JSON.stringify(value) : String(value),
          };
        }, {} as Record<string, string>),
        ...operatorMetadata,
      };

      const orderResult = await dispatch(
        finalizeOrder({
          orderNumber: order.number ?? "",
          orderId: order.id,
          amount: totalMonthlyAmount,
          email: email,
          phone: bankIdContact.phone,
          customerId: customerId,
          currency: "sek",
          items: filteredItems,
          billing: {
            first_name: order.billing_address?.first_name ?? "",
            last_name: order.billing_address?.last_name ?? "",
            street: order.billing_address?.line_1 ?? "",
            postal_code: order.billing_address?.zip_code ?? "",
            city: order.billing_address?.city ?? "",
            country: order.billing_address?.country_code ?? "",
          },
          metafields: orderMetadata,
          promotion: order.metadata?.promotion,
        })
      ).unwrap();
      console.log("orderResult", orderResult);

      clearCheckout3dsSnapshot();
      onSuccess(orderResult.clOrderId);
    },
    [
      order,
      bankIdContact.email,
      bankIdContact.phone,
      verificationDetails,
      dispatch,
      navigate,
      lang,
      selectedPlan,
      operator,
      phoneDetails.numberType,
      phoneDetails.phoneNumber,
      promotionVoucher,
      onSuccess,
      shouldBypassCreditCheckForTest,
      t,
    ]
  );

  const toErrorMessage = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === "string") return err;
    if (err && typeof err === "object" && "message" in err) {
      const msg = (err as { message?: unknown }).message;
      if (typeof msg === "string") return msg;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return t("errors.unknown");
    }
  };

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!stripe || !isActive) return;

    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("setup_intent_client_secret");
    const redirectStatus = params.get("redirect_status");

    if (!clientSecret) return;

    if (!verificationDetails?.userAttributes?.personalNumber) return;

    let cancelled = false;

    (async () => {
      stripStripe3dsReturnParams();

      if (redirectStatus === "failed") {
        if (!cancelled) {
          setError(stripeSetupErrorMessages.authentication_required);
          setNeedsRetry(true);
          clearCheckout3dsSnapshot();
          dispatch(setSubmitLoading(false));
        }
        return;
      }

      try {
        dispatch(setSubmitLoading(true));
        const { setupIntent, error: retrieveError } =
          await stripe.retrieveSetupIntent(clientSecret);

        if (cancelled) return;

        if (retrieveError) {
          const errCode =
            retrieveError.decline_code || retrieveError.code || "unknown";
          throw new Error(
            stripeSetupErrorMessages[errCode] || retrieveError.message
          );
        }

        if (
          !setupIntent ||
          setupIntent.status !== "succeeded" ||
          !setupIntent.payment_method
        ) {
          const lastErr = setupIntent?.last_setup_error;
          const errCode =
            lastErr?.decline_code || lastErr?.code || "authentication_required";
          throw new Error(
            stripeSetupErrorMessages[errCode] ||
              lastErr?.message ||
              stripeSetupErrorMessages.authentication_required
          );
        }

        const customerIdRaw = (
          setupIntent as { customer?: string | { id: string } | null }
        ).customer;
        const customerId =
          typeof customerIdRaw === "string"
            ? customerIdRaw
            : customerIdRaw &&
              typeof customerIdRaw === "object" &&
              "id" in customerIdRaw
            ? (customerIdRaw as { id: string }).id
            : null;

        if (!customerId) {
          throw new Error(t("cardCheckout.missingCustomerAfterPayment"));
        }

        const paymentMethodId =
          typeof setupIntent.payment_method === "string"
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;

        await finalizeCheckoutAfterSetup(customerId, paymentMethodId);
      } catch (err) {
        if (!cancelled) {
          console.error("3DS return / retrieveSetupIntent:", err);
          clearCheckout3dsSnapshot();
          setError(toErrorMessage(err));
          setNeedsRetry(true);
          dispatch(setSubmitLoading(false));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    stripe,
    isActive,
    verificationDetails?.userAttributes?.personalNumber,
    dispatch,
    finalizeCheckoutAfterSetup,
    stripeSetupErrorMessages,
  ]);

  // TODO: Refactor this to be more readable and move to a useCheckout hook
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError(t("cardCheckout.stripeNotLoaded"));
      setNeedsRetry(true);
      return;
    }

    clearError();
    // After the user clicks retry/submit again, we revert the CTA label.
    setNeedsRetry(false);

    // Use order email or fallback to BankID email to prevent race condition issues
    const email = order.customer_email || bankIdContact.email;
    if (!email) {
      setError(t("cardCheckout.missingCustomerEmail"));
      return;
    }

    try {
      // 1. Validate form first
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;

      if (!verificationDetails?.userAttributes.personalNumber) {
        throw new Error(t("cardCheckout.missingPersonalNumber"));
      }
      // Only start loading after validation succeeds
      dispatch(setSubmitLoading(true));

      // Handle newsletter subscription if checkbox is checked
      if (checkboxChecked.marketing) {
        try {
          await dispatch(
            subscribeToNewsletter({
              email: email,
              subscribe: true,
            })
          ).unwrap();
        } catch (error) {
          // Log error but don't stop the main flow
          console.error("Newsletter subscription failed:", error);
        }
      }

      // 1. Create setup intent - this might fail if credit check fails or other payment issues
      const setupResult = await dispatch(
        initiateCheckout({
          email: email,
          firstName: order.billing_address?.first_name || "",
          lastName: order.billing_address?.last_name || "",
        })
      ).unwrap();

      saveCheckout3dsSnapshot({
        orderId: order.id,
        verificationDetails,
        bankIdContact: {
          email: bankIdContact.email,
          phone: bankIdContact.phone,
        },
        bankIdStepData: stepStates[CheckoutStep.BANK_ID].data,
        deliveryStepData: stepStates[CheckoutStep.DELIVERY].data,
      });

      // 2. Confirm setup with stripe and set as default payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: setupResult.clientSecret,
        confirmParams: {
          // Keep bypass flags across Stripe redirect so the credit-check decision
          // is consistent after `retrieveSetupIntent` runs on the return page.
          return_url: shouldBypassCreditCheckForTest
            ? `${window.location.origin}/${lang}/${order.id}?skip-bankid=1`
            : `${window.location.origin}/${lang}/${order.id}`,
          payment_method_data: {
            billing_details: {
              email: email,
              name: `${order.billing_address?.first_name || ""} ${
                order.billing_address?.last_name || ""
              }`.trim(),
              address: {
                city: order.billing_address?.city || "",
                country: order.billing_address?.country_code || "",
                line1: order.billing_address?.line_1 || "",
                postal_code: order.billing_address?.zip_code || "",
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (confirmError) {
        console.error("Stripe confirmSetup error:", {
          code: confirmError.code,
          decline_code: confirmError.decline_code,
          message: confirmError.message,
          type: confirmError.type,
        });

        const errorCode =
          confirmError.decline_code || confirmError.code || "unknown";
        const errorMessage =
          stripeSetupErrorMessages[errorCode] || confirmError.message;
        throw new Error(errorMessage);
      }

      if (!setupIntent?.payment_method) {
        throw new Error(t("cardCheckout.missingPaymentMethod"));
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

      clearCheckout3dsSnapshot();
      await finalizeCheckoutAfterSetup(setupResult.customerId, paymentMethodId);
    } catch (err) {
      console.log("Error submitting payment form:", JSON.stringify(err));
      clearCheckout3dsSnapshot();
      setError(toErrorMessage(err));
      setNeedsRetry(true);
      dispatch(setCreatingOrder(false));
      dispatch(setSubmitLoading(false));
    }
  };

  const getSubmitState = () => {
    // If only phone is selected, enable submit
    if (!operator) {
      return { enabled: true, message: null };
    }

    // If an operator is selected but no plan
    if (operator && !selectedPlan) {
      return {
        enabled: false,
        message: t("cardCheckout.selectOperatorRequired"),
      };
    }

    // If plan selected but no number type chosen
    if (!phoneDetails.numberType) {
      return {
        enabled: false,
        message: t("cardCheckout.selectPhoneNumberRequired", {
          operator: operatorName,
        }),
      };
    }

    // If new number selected
    if (phoneDetails.numberType === "new") {
      return { enabled: true, message: null };
    }

    // If port number selected but no number provided or has error
    if (phoneDetails.numberType === "port") {
      if (!phoneDetails.phoneNumber) {
        return {
          enabled: false,
          message: t("cardCheckout.portNumberRequired"),
        };
      }
      if (phoneDetails.error) {
        return {
          enabled: false,
          message: phoneDetails.error,
        };
      }
      return { enabled: true, message: null };
    }

    return { enabled: false, message: t("errors.generic") };
  };

  const { enabled, message } = getSubmitState();

  {
    /* 
      Disable submit button if:
      - !stripe || !elements -> Stripe SDK not initialized yet
      - submitLoading -> Prevent double submission while processing
      - !enabled -> Business logic validation (operator/plan selection) failed
      - !isComplete -> Stripe payment form is incomplete
    */
  }
  const submitDisabled =
    !stripe || !elements || submitLoading || !enabled || !isComplete;

  if (!isActive) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>{t("errors.title")}</AlertTitle>
            <AlertDescription>{t("errors.generic")}</AlertDescription>
          </Alert>
        )} */}

        {/* {!isReady && (
          <div className="py-4 flex justify-center">
            <Loader2 className="animate-spin" />
          </div>
        )} */}

        {/* <p className="text-sm">
          Genom att ange kortinformationen ger du Refurbly AB tillåtelse att
          debitera ditt kort för framtida betalningar enligt våra villkor.
        </p> */}

        <PaymentElement
          className={!isReady ? "hidden" : ""}
          onChange={(event) => {
            setIsComplete(event.complete);
            // If the user edits card details after an error, remove the old error message.
            // This avoids "stuck" errors and makes the main submit button the single retry CTA.
            if (error) {
              clearError();
            }
          }}
          onReady={() => setIsReady(true)}
        />

        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>{t("errors.title")}</AlertTitle>
            <AlertDescription>
              <div className="space-y-2">
                <div>{error}</div>
                <div className="text-sm">
                  {t("errors.supportContact")}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {message && (
          <div
            className="flex items-start gap-2 rounded-lg bg-[lightyellow] p-2.5 text-sm text-red-700"
            role="status"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <span>{message}</span>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Checkbox
            id="terms"
            checked={checkboxChecked.terms}
            onCheckedChange={(checked) =>
              setCheckboxChecked({
                ...checkboxChecked,
                terms: checked as boolean,
              })
            }
          />
          <label
            htmlFor="terms"
            className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("cardCheckout.termsBefore")}
            <a
              href={resolveFlexSiteUrl("/hyresvillkor", lang)}
              className="underline"
            >
              {t("cardCheckout.termsLink")}
            </a>
            {t("cardCheckout.termsAnd")}
            <a
              href={resolveFlexSiteUrl("/forsakringsvillkor", lang)}
              className="underline"
            >
              {t("cardCheckout.insuranceLink")}
            </a>
            .
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="marketing"
            checked={checkboxChecked.marketing}
            onCheckedChange={(checked) =>
              setCheckboxChecked({
                ...checkboxChecked,
                marketing: checked as boolean,
              })
            }
          />
          <label
            htmlFor="marketing"
            className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("cardCheckout.newsletter")}
          </label>
        </div>

        <Button
          type="submit"
          variant={"secondary"}
          className="w-full"
          size="lg"
          color="primary"
          disabled={submitDisabled || !checkboxChecked.terms || !isReady}
        >
          {submitLoading && !createOrderStatus.loading ? (
            <Loader2 className="animate-spin" />
          ) : null}
          <span>
            {submitLoading && !createOrderStatus.loading
              ? t("cardCheckout.submitLoading")
              : needsRetry
                ? t("cardCheckout.tryAgain")
                : t("cardCheckout.submit")}
          </span>
        </Button>

        <p className="text-xs text-center text-gray-700">
          {t("cardCheckout.creditCheckNotice")} <br />
          {t("cardCheckout.creditSafeNotice")}
        </p>
      </div>
    </form>
  );
}
